import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowLeft, FiPackage, FiDollarSign, FiCalendar, FiTruck, FiTrash2 } from 'react-icons/fi'

export default function PaymentsOrdersPage() {
  const { websiteId } = useParams()
  const navigate = useNavigate()

  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState(-1)

  useEffect(() => {
    fetchOrders()
  }, [websiteId, page, sortBy, sortOrder])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.append('page', page)
      params.append('limit', 10)
      params.append('sortBy', sortBy)
      params.append('sortOrder', sortOrder)

      console.log('[PaymentsOrders] Fetching orders for website:', websiteId, 'with params:', {
        page,
        limit: 10,
        sortBy,
        sortOrder
      })

      const ordersResponse = await axios.get(`/websites/${websiteId}/orders?${params}`)
      
      setOrders(ordersResponse.data.data || [])
      setTotalPages(ordersResponse.data.pagination?.pages || 1)
      
      console.log('[PaymentsOrders] Fetched successfully:', {
        ordersCount: ordersResponse.data.data?.length || 0,
        totalPages: ordersResponse.data.pagination?.pages || 1,
        totalOrders: ordersResponse.data.pagination?.total || 0
      })
    } catch (error) {
      console.error('[PaymentsOrders] Error fetching:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        error: error.message,
        fullError: error.response?.data
      })
      
      if (error.response?.status === 404) {
        console.log('[PaymentsOrders] 404 - No orders found or website not found')
        setOrders([])
      } else if (error.response?.status === 403) {
        console.error('[PaymentsOrders] 403 - Permission denied. Check authentication.')
        toast.error('Permission denied. Please ensure you are logged in.')
      } else if (error.response?.status === 401) {
        console.error('[PaymentsOrders] 401 - Unauthorized. Token may be expired.')
        toast.error('Session expired. Please log in again.')
      } else {
        console.error('[PaymentsOrders] Unexpected error:', error.response?.status || 'Unknown')
        toast.error('Failed to load orders')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return
    }

    try {
      await axios.delete(`/websites/${websiteId}/orders/${orderId}`)
      toast.success('Order deleted successfully')
      
      // Remove from local state
      setOrders(orders.filter(order => order._id !== orderId))
      
      // If the deleted order was selected, clear selection
      if (selectedOrder?._id === orderId) {
        setSelectedOrder(null)
      }

      // Refresh the orders list to ensure pagination is correct
      setTimeout(() => {
        fetchOrders()
      }, 300)
    } catch (error) {
      console.error('[PaymentsOrders] Error deleting order:', error)
      toast.error('Failed to delete order')
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-400'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-400'
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-400'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-400'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed'
      case 'pending':
        return 'Pending'
      case 'failed':
        return 'Failed'
      default:
        return status || 'Unknown'
    }
  }

  const formatCurrency = (amount, currency = 'NGN') => {
    const symbols = {
      NGN: '₦',
      USD: '$',
      EUR: '€',
      GBP: '£'
    }
    const symbol = symbols[currency] || currency

    return `${symbol}${(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/user-admin/websites')}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Back to Website Builder Dashboard"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiPackage className="text-green-600" />
            Orders & Payments
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {orders.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Orders List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow">
                {/* Sort Controls */}
                <div className="p-4 border-b border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort by:
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value)
                      setPage(1)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="createdAt">Date (Newest)</option>
                    <option value="total">Total Amount</option>
                    <option value="visitorName">Customer Name</option>
                  </select>
                </div>

                {/* Orders */}
                <div className="divide-y divide-gray-200 max-h-screen overflow-y-auto">
                  {orders.map((order) => (
                    <button
                      key={order._id}
                      onClick={() => setSelectedOrder(order)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition border-l-4 ${getStatusColor(order.paymentStatus)} ${
                        selectedOrder?._id === order._id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">
                            {order.visitorName}
                          </div>
                          <div className="text-xs text-gray-500">#{order.orderId}</div>
                          <div className="mt-2 text-sm">
                            <span className="text-gray-700 font-medium">
                              {formatCurrency(order.total, order.currency)}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 text-right">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          {order.items?.length || 0} items
                        </span>
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getStatusColor(order.paymentStatus)}`}>
                          {getStatusText(order.paymentStatus)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-gray-200 flex gap-2 justify-center">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      {page} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Order Detail */}
            {selectedOrder ? (
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow p-6 space-y-6">
                  {/* Header */}
                  <div className="flex items-start justify-between pb-6 border-b border-gray-200">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Order #{selectedOrder.orderId}</h2>
                      <p className="text-gray-600 mt-1">
                        {new Date(selectedOrder.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleDeleteOrder(selectedOrder._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Order"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                      <span className={`inline-block px-3 py-1 font-medium rounded ${getStatusColor(selectedOrder.paymentStatus)}`}>
                        {getStatusText(selectedOrder.paymentStatus)}
                      </span>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Name
                      </label>
                      <p className="text-gray-900">{selectedOrder.visitorName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <a
                        href={`mailto:${selectedOrder.visitorEmail}`}
                        className="text-blue-600 hover:underline"
                      >
                        {selectedOrder.visitorEmail}
                      </a>
                    </div>
                    {selectedOrder.visitorPhone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <a
                          href={`tel:${selectedOrder.visitorPhone}`}
                          className="text-blue-600 hover:underline"
                        >
                          {selectedOrder.visitorPhone}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Items */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiPackage className="w-4 h-4" />
                      Items ({selectedOrder.items?.length || 0})
                    </h3>
                    <div className="space-y-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      {selectedOrder.items && selectedOrder.items.length > 0 ? (
                        selectedOrder.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-start pb-2 border-b border-gray-200 last:border-b-0"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-600">
                                Qty: {item.quantity} × {formatCurrency(item.price, selectedOrder.currency)}
                              </p>
                            </div>
                            <p className="font-semibold text-gray-900">
                              {formatCurrency(item.quantity * item.price, selectedOrder.currency)}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No items in this order</p>
                      )}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  {selectedOrder.shipping && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FiTruck className="w-4 h-4" />
                        Shipping Address
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-1">
                        <p className="text-gray-900">{selectedOrder.shipping.address}</p>
                        <p className="text-gray-900">
                          {selectedOrder.shipping.city}, {selectedOrder.shipping.state}{' '}
                          {selectedOrder.shipping.zip}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Payment Summary */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FiDollarSign className="w-4 h-4" />
                      Payment Summary
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Subtotal:</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(selectedOrder.subtotal, selectedOrder.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Tax:</span>
                        {selectedOrder.tax > 0 ? (
                          <span className="font-medium text-gray-900">
                            {formatCurrency(selectedOrder.tax, selectedOrder.currency)}
                          </span>
                        ) : (
                          <span className="font-medium text-gray-900 italic text-green-600">Tax Included</span>
                        )}
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="font-semibold text-gray-900">Total:</span>
                        <span className="font-bold text-lg text-green-600">
                          {formatCurrency(selectedOrder.total, selectedOrder.currency)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  {selectedOrder.paystackReference && (
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <span className="font-semibold">Paystack Reference:</span> {selectedOrder.paystackReference}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="lg:col-span-2 bg-white rounded-lg shadow p-8 flex items-center justify-center h-96">
                <p className="text-gray-500 text-center">
                  Select an order to view details
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiPackage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Orders Yet</h2>
            <p className="text-gray-600">
              Orders will appear here once customers make purchases.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
